import {diff} from 'deep-object-diff';
import {Subject, Subscription} from 'rxjs';
import produce from 'immer';
import {isEmpty, set, startsWith} from 'lodash';

import {_expandObject, _flattenObject, collaborationLog} from './collaboration.utils';
import {CollaborationWebSocket} from './CollaborationWebSocket';
import {
  CE_AWARENESS_CHANGE,
  CE_CONNECTION_ATTEMPT,
  CE_DIFF_SYNCHRONIZER_CREATED,
  CE_DISCONNECT,
  CE_ERROR,
  CE_FROM_DISCONNECT_SYNC_BEGIN,
  CE_LOCAL_UPDATED_BEGIN,
  CE_NEW_LOCK_STATE,
  CE_NEW_SERVER_STATE,
  CE_NEW_STATE,
  CE_STATELESS,
  CE_STATUS_MODIFICATION_EXCEPTION,
  CE_SYNC_DISCONNECT,
  CE_TO_LIMBO,
  CW_STATUS_CHANGED,
  CW_STATUS_CHANGED_BROADCAST,
  CW_VERSION_CHANGED_BROADCAST,
  CW_VERSION_REQUEST,
  TIMEOUTS,
} from './types';
import {FirehoseHandle} from './FirehoseHandle';

export const HP_LOCAL_UPDATE_BEGIN = 'localUpdateBegin';

/**
 *
 * This class would not be necessary if we allowed the server
 * to have a consistent history across the document such that there
 * would be just one yjs document we could depend on.
 *
 * Responsible for
 * 1. managing diffs and
 * 2. reconnecting multiple hocuspocus instances
 * 3. flattening the document
 * 4. making sure local updates are staged for saving
 *
 * Not Responsible for
 * 1. triggering the reconnect.
 *        The caller should call connectNew() in that case.
 * 2. deciding if we should give up trying to connect.
 *        The caller should not call connectNew if you gave up.
 * 3. managing error timeouts
 *        The caller should listen to CE_NEW_STATE to
 *          both get latest saved state and know that we are connected
 *
 * Has two states that are controlled by caller/server error
 *
 * 1. connected === true: sending updates to yjs
 * 2. connected === false: collecting all updates, not sending to yjs
 *
 * it does this by constantly storing off last known good
 * from websocket. CollaborationWebSocket will send CE_NEW_SERVER_STATE
 * whenever
 * 1. an initial sync completed, or
 * 2. a remote update came in, or
 * 3. the local changes have been saved
 *
 * so depending on this, DiffSynchronizer will call CE_NEW_STATE
 * on any one of those events *except* if a remote update came in
 * and we were offline AND we have local changes that were not synced
 *
 * in that case, we send our offline changes, then when those are saved,
 * CE_NEW_STATE is triggered again.
 *
 */
export class DiffSynchronizer {
  /**
   *
   * @param {Subject} firehose the event bus
   * @param {string} name the document name
   * @param {string} status (optional) the status the document should be pinned to
   */
  constructor(firehose, name, status) {
    this.fh = new FirehoseHandle(firehose, name);
    this.connectCounter = 0;
    this.shouldConnect = true;
    this.firehose = firehose;
    this.name = name;
    this.websocket = new CollaborationWebSocket(firehose, name);
    this.connected = false;
    /**
     * lastGoodState represents the last state
     * the server has sent us
     */
    this.lastGoodState = undefined;
    /**
     * _myCurrentState represents the latest changes
     * both saved and unsaved to server. it represents
     * the json view of the current local yjs document
     */
    this._myCurrentState = undefined;
    this.messageTimeout = 30000;
    this.status = status;
    this.versionId = '';
    this.versionIdMissingCheck = TIMEOUTS.versionIdMissingCheck;
    this.fh.sendEvent(CE_DIFF_SYNCHRONIZER_CREATED);
  }
  // starts a new connection if we don't have one
  connectNew(connectOptions) {
    return new Promise((resolve, reject) => {
      this.fh.sendEvent(CE_CONNECTION_ATTEMPT, this.connectCounter);
      this.connectCounter++;
      if (!this.shouldConnect) {
        let message = 'connectNew called on a closed DiffSynchronizer';
        this.fh.sendError(message);
        reject(message);
        return;
      }
      if (this.connected) {
        let message = 'connectNew called on an open DiffSynchronizer';
        this.fh.sendError(message);
        reject(message);
        return;
      }
      let toResolve = true;
      if (this.websocket.hasConnectAttempt) {
        // destroy and make new
        // will also close all the subscribers
        this.websocket.close();
        this.websocket = new CollaborationWebSocket(this.firehose, this.name);
      }
      this.websocket.fh.on(CE_DISCONNECT, () => {
        if (this.connected) {
          this.connected = false;
          this.versionId = '';
          this.websocket.close();
          let message = 'CE_DISCONNECT while connected';
          this.fh.sendEvent(CE_SYNC_DISCONNECT, message);
          this.fh.sendError(message);
        }
      });
      this.websocket.fh.on(CE_ERROR, (message) => {
        if (this.connected) {
          this.connected = false;
          this.versionId = '';
          console.log('app-collaboration CE_ERROR while connected: ', message);
          this.websocket.close();
          this.fh.sendEvent(CE_SYNC_DISCONNECT, message);
        }
        this.fh.sendError(message);
        toResolve && reject(message);
        toResolve = false;
      });
      this.websocket.fh.on(CE_NEW_SERVER_STATE, (newFlatState) => {
        if (!isEmpty(newFlatState)) {
          this.status = this.status || newFlatState.__status;
          if (this.status !== newFlatState.__status) {
            let statusModification = {
              shouldBe: this.status,
              grantId: newFlatState.__grantId,
              subGrantId: newFlatState.__subGrantId,
              entityType: newFlatState.__entityType,
              found: newFlatState.__status,
              who: newFlatState.__modifiedBy,
            };
            console.log(
              'app-collaboration status modification exception',
              statusModification
            );
            this.shouldConnect = false;
            this.websocket.close();
            this.fh.sendEvent(CE_STATUS_MODIFICATION_EXCEPTION, statusModification);
            toResolve && reject(CE_STATUS_MODIFICATION_EXCEPTION);
            toResolve = false;
            return;
          }
        }
        this.newServerState(newFlatState);
        toResolve && resolve('connected');
        toResolve = false;
      });
      this.websocket.fh.on(CE_NEW_LOCK_STATE, (lockInfo) => {
        this.fh.sendEvent(CE_NEW_LOCK_STATE, lockInfo);
      });
      this.websocket.fh.on(CE_AWARENESS_CHANGE, ({remotes}) => {
        this.fh.sendEvent(CE_AWARENESS_CHANGE, {remotes});
      });
      this.websocket.fh.on(CE_STATELESS, (payload) => {
        const serverStatusChange = `${CW_STATUS_CHANGED_BROADCAST}/${CW_STATUS_CHANGED}/`;
        const versionChange = `${CW_VERSION_CHANGED_BROADCAST}/`;
        if (startsWith(payload, serverStatusChange)) {
          const statusModification = JSON.parse(payload.split(serverStatusChange)[1]);
          this.fh.sendEvent(CE_STATUS_MODIFICATION_EXCEPTION, statusModification);
        } else if (startsWith(payload, versionChange)) {
          this.versionId = payload.split(versionChange)[1];
          if (this.connected) this.raiseState();
        }
      });
      this.websocket.connect(connectOptions);
    });
  }

  newServerState(newFlatState) {
    if (isEmpty(newFlatState)) {
      // server connected, but we don't have data yet
      // leave connected/lastGoodState alone and wait for next call
      // with actual server data.

      // this occurs when connecting to a pod that doesn't
      // have the room data yet, and is expecting it
      // to come from another pod, which normally happens
      // after about 20-400 milliseconds in practice

      // The server, on empty backend data, (example new application or no attachments),
      //  should have *some* data
      // example, _flattenObject({}) = { __: '5d60f178377e' }; which is not empty
      collaborationLog(
        `TRACE: ${this.name} ${this.fh.instanceId} DiffSynchronizer#newServerState called with empty server state`
      );
      return;
    }
    let reconnectChanges = this.handleReconnect(newFlatState);
    if (reconnectChanges) return; // don't change anything else
    if (!this.connected) this.startVersionTimeout();
    this.connected = true;
    // store new server state as our state.
    this._myCurrentState = newFlatState;
    this.lastGoodState = newFlatState;
    // broadcast our new state
    this.raiseState();
  }

  startVersionTimeout() {
    let ds = this;
    setTimeout(() => {
      if (!ds.versionId) {
        this.sendStateless(CW_VERSION_REQUEST);
      }
    }, ds.versionIdMissingCheck);
  }

  raiseState() {
    let newState = _expandObject(this._myCurrentState);
    if (this.versionId) {
      newState.versionId = this.versionId;
    }
    this.fh.sendEvent(CE_NEW_STATE, newState);
  }

  offlineChanges() {
    return diff(this.lastGoodState, this._myCurrentState);
  }

  /**
   * @returns {boolean} true if we handled the reconnect
   */
  handleReconnect(newFlatState) {
    if (this.connected || !this.lastGoodState) {
      // we are not reconnecting
      return false;
    }
    // we have a last known good state
    // get the diff of what we did while we were offline
    const diffResults = this.offlineChanges();
    if (isEmpty(diffResults)) {
      // no local changes to save!!!
      return false;
    }
    // apply that diff to new state
    const newLocalUpdated = produce(newFlatState, (draft) => {
      const keys = Object.keys(diffResults);
      for (const key of keys) {
        set(draft, key, diffResults[key]);
      }
    });
    // which of our local changes did the server not have?
    const newDiffResults = diff(newFlatState, newLocalUpdated);

    if (isEmpty(newDiffResults)) {
      // all of our local changes were actually saved!
      return false;
    }
    this.fh.sendEvent(CE_FROM_DISCONNECT_SYNC_BEGIN, {offlineChanges: newDiffResults});
    // reset state to what we got from the server, then
    // send all the diffs we made offline again now that we are connected
    this.startVersionTimeout();
    this.connected = true;
    this.lastGoodState = newFlatState;
    this._myCurrentState = newFlatState;
    this.localUpdatedFlatState(newLocalUpdated);
    // do not raise that we have a good state yet, as we need to
    // make sure our offline changes got saved, so let this method be called again
    // after the above changes are saved
    return true;
  }

  localUpdated(newState) {
    this.fh.sendEvent(CE_LOCAL_UPDATED_BEGIN, newState);
    let myState = {...newState};
    if (myState.versionId) {
      delete myState.versionId;
    }
    const newFlatState = _flattenObject(myState);
    this.localUpdatedFlatState(newFlatState);
  }

  localUpdatedFlatState(newFlatStateUnclean) {
    //check to see it's a valid flat state
    try {
      const expanded = _expandObject(newFlatStateUnclean);
      if (!expanded) throw 'expanded to empty object';
      const newFlatState = _flattenObject(expanded);
      const diffResults = diff(this._myCurrentState, newFlatState);
      this._myCurrentState = newFlatState;
      if (this.connected) {
        this.websocket.localUpdated(diffResults);
      }
    } catch (error) {
      console.error('app-collaboration flatValidation failure', error);
      this.fh.sendError(`unable to validate flat state`);
      return;
    }
  }

  close() {
    this.shouldConnect = false;
    this.websocket.close();
    this.fh.close();
  }

  setLocalAwarenessState(localAwarenessState) {
    if (this.connected) {
      this.websocket.setLocalAwarenessState(localAwarenessState);
    }
  }

  sendStateless(message) {
    if (this.connected) this.websocket.sendStateless(message);
  }
}
